import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Box } from "@mui/material";

export type SignaturePadHandle = {
  clear: () => void;
  toDataUrl: (type?: string) => string | null;
  isEmpty: () => boolean;
};

export type SignaturePadProps = {
  onInkChange?: (hasInk: boolean) => void;
};

const SIGNATURE_CANVAS_HEIGHT = 200;

/** Pen-tip cursor for signing (hotspot at nib). Falls back to crosshair if unsupported. */
const SIGNATURE_PEN_CURSOR =
  'url(\'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%23000" d="M20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83zM3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/%3E%3C/svg%3E\') 2 22, crosshair';

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  function SignaturePad({ onInkChange }, ref) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isDrawingRef = useRef(false);
    const lastPointRef = useRef<{ x: number; y: number } | null>(null);
    const hasInkRef = useRef(false);
    const dprRef = useRef(1);

    const setHasInk = useCallback(
      (hasInk: boolean) => {
        hasInkRef.current = hasInk;
        onInkChange?.(hasInk);
      },
      [onInkChange],
    );

    const setupCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(Math.floor(rect.width), 320);
      const height = SIGNATURE_CANVAS_HEIGHT;
      const dpr = window.devicePixelRatio || 1;
      dprRef.current = dpr;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }, []);

    const clearCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      isDrawingRef.current = false;
      lastPointRef.current = null;
      setHasInk(false);
    }, [setHasInk]);

    const toDataUrl = useCallback((type = "image/png") => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      try {
        return canvas.toDataURL(type);
      } catch {
        return null;
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        clear: clearCanvas,
        toDataUrl,
        isEmpty: () => !hasInkRef.current,
      }),
      [clearCanvas, toDataUrl],
    );

    useEffect(() => {
      setupCanvas();
    }, [setupCanvas]);

    const getCanvasPoint = (
      clientX: number,
      clientY: number,
    ): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const dpr = dprRef.current || 1;
      const scaleX = canvas.width / rect.width / dpr;
      const scaleY = canvas.height / rect.height / dpr;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    };

    const drawLineTo = (x: number, y: number) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      const last = lastPointRef.current;
      if (!ctx || !last) return;
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(x, y);
      ctx.stroke();
      lastPointRef.current = { x, y };
      if (!hasInkRef.current) {
        setHasInk(true);
      }
    };

    const startStroke = (clientX: number, clientY: number) => {
      const point = getCanvasPoint(clientX, clientY);
      if (!point) return;
      isDrawingRef.current = true;
      lastPointRef.current = point;
    };

    const continueStroke = (clientX: number, clientY: number) => {
      if (!isDrawingRef.current) return;
      const point = getCanvasPoint(clientX, clientY);
      if (!point) return;
      drawLineTo(point.x, point.y);
    };

    const endStroke = () => {
      isDrawingRef.current = false;
      lastPointRef.current = null;
    };

    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 560,
          mx: "auto",
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          onMouseDown={(e) => startStroke(e.clientX, e.clientY)}
          onMouseMove={(e) => continueStroke(e.clientX, e.clientY)}
          onMouseUp={endStroke}
          onMouseLeave={endStroke}
          onTouchStart={(e) => {
            e.preventDefault();
            const touch = e.touches[0];
            if (touch) startStroke(touch.clientX, touch.clientY);
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            const touch = e.touches[0];
            if (touch) continueStroke(touch.clientX, touch.clientY);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            endStroke();
          }}
          sx={{
            display: "block",
            width: "100%",
            height: SIGNATURE_CANVAS_HEIGHT,
            touchAction: "none",
            cursor: SIGNATURE_PEN_CURSOR,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "#fff",
          }}
        />
      </Box>
    );
  },
);
